import { memo, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { Archive } from '../../api/archives';
import { clipDuration, formatTime } from '../../format/helpers';
import {
  MIN_CLIP_DURATION,
  moveClipToTrack,
  rippleLaterClips,
  snapTargets,
  snapTime,
} from '../../format/invariants';
import { clipKeyframeTimes, retimeKeyframesAt } from '../../format/params';
import type { AnyClip, AudioClip, OverlayClip, Track, VideoClip } from '../../format/types';
import { usePlayback } from '../../state/playback';
import { findClip, useTimeline } from '../../state/timeline';
import { SNAP_PX, type DragMode, type LaneBand } from './constants';
import { useTimelineCtx } from './context';
import { CacheBar } from './CacheBar';
import { ClipStrip, sourceTitle } from './ClipStrip';

interface ClipProps {
  clip: AnyClip;
  trackId: string;
  trackKind: Track['kind'];
  archives: Archive[];
}

/**
 * Memoized: on a long timeline every remote update re-renders the lane, and
 * only the clips whose object identity changed should pay. Everything the
 * component needs from the wider edit is either subscribed narrowly (its own
 * source) or read from the store at event time (drag setup) — an `edit` prop
 * here would defeat the memo, since edit identity changes on every mutation.
 */
export const ClipBlock = memo(function ClipBlock({
  clip,
  trackId,
  trackKind,
  archives,
}: ClipProps) {
  const { pps, laneRefs, setDropTrack } = useTimelineCtx();
  const selected = useTimeline((s) => s.selection.includes(clip.id));
  const source = useTimeline((s) => ('kind' in clip ? undefined : s.edit.sources[clip.source]));
  const dur = clipDuration(clip);
  const left = clip.start * pps;
  const width = Math.max(4, dur * pps);

  const dragState = useRef<{
    mode: DragMode;
    startX: number;
    startY: number;
    moved: boolean;
    orig: AnyClip;
    targets: number[];
    shift: boolean;
    alt: boolean;
    lanes: LaneBand[];
    /** same-kind lane currently under the pointer (origin track by default) */
    hoverTrackId: string;
    /** clips moved together (the primary + any co-selected clips) with their originals */
    group: { id: string; orig: AnyClip }[];
  } | null>(null);

  function onPointerDown(mode: DragMode) {
    return (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      const playhead = usePlayback.getState().currentTime;
      // Drag setup wants the whole edit (lane geometry, snap targets, group
      // originals) — read it at event time so it is not a render-time prop.
      const edit = useTimeline.getState().edit;
      const lanes: LaneBand[] =
        mode === 'move'
          ? edit.tracks
              .filter((t) => t.kind === trackKind)
              .flatMap((t) => {
                const el = laneRefs.current.get(t.id);
                if (!el) return [];
                const r = el.getBoundingClientRect();
                return [{ trackId: t.id, top: r.top, bottom: r.bottom }];
              })
          : [];
      // Multi-clip move: drag every co-selected clip as a rigid group. Only for
      // 'move' (trims stay single-clip). Snap targets exclude all group ids.
      const selection = useTimeline.getState().selection;
      const groupIds =
        mode === 'move' && selection.length > 1 && selection.includes(clip.id)
          ? selection
          : [clip.id];
      const group = groupIds
        .map((id) => {
          const loc = findClip(edit, id);
          return loc ? { id, orig: structuredClone(loc.clip) } : null;
        })
        .filter((g): g is { id: string; orig: AnyClip } => g !== null);
      dragState.current = {
        mode,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        orig: structuredClone(clip),
        targets: snapTargets(edit.tracks, playhead, groupIds),
        shift: e.shiftKey,
        alt: e.altKey,
        lanes,
        hoverTrackId: trackId,
        group,
      };
      // window-level listeners (not element pointer capture): a cross-lane
      // move re-parents this clip's DOM node mid-drag, which would silently
      // drop element-level capture and strand the drag
      const onMove = (ev: PointerEvent) => updateDrag(ev.clientX, ev.clientY);
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        endDrag();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };
  }

  function updateDrag(clientX: number, clientY: number) {
    const st = dragState.current;
    if (!st) return;
    const dxPx = clientX - st.startX;
    if (!st.moved) {
      // vertical-only drags (cross-track moves) must start the drag too
      if (Math.abs(dxPx) < 3 && Math.abs(clientY - st.startY) < 3) return;
      st.moved = true;
      useTimeline.getState().beginTransient();
    }
    const dt = dxPx / pps;
    const threshold = SNAP_PX / pps;
    const tlState = useTimeline.getState();

    // vertical: which same-kind lane is hovered? (sticky — keeps the last
    // hovered lane while the pointer is between/outside lanes)
    if (st.mode === 'move' && st.lanes.length > 1) {
      const band = st.lanes.find((b) => clientY >= b.top && clientY < b.bottom);
      if (band) st.hoverTrackId = band.trackId;
    }
    // cross-track re-homing only for a single-clip drag (multi-clip vertical
    // reassignment across non-uniform tracks is ambiguous — keep it horizontal)
    const crossTrackId =
      st.mode === 'move' && st.group.length === 1 && st.hoverTrackId !== trackId
        ? st.hoverTrackId
        : null;

    tlState.updateTransient((draft) => {
      const loc = findClip(draft, clip.id);
      if (!loc) return;
      const c = loc.clip;
      const orig = st.orig;
      const origDur = clipDuration(orig);

      if (st.mode === 'move') {
        let ns = Math.max(0, orig.start + dt);
        // snap either edge of the PRIMARY clip, pick the closer
        const s1 = snapTime(ns, st.targets, threshold);
        const s2 = snapTime(ns + origDur, st.targets, threshold) - origDur;
        if (Math.abs(s2 - ns) < Math.abs(s1 - ns)) ns = Math.max(0, s2);
        else ns = Math.max(0, s1);
        // apply the same delta to the whole group, clamped so nothing goes < 0
        // (keeps the group rigid rather than clamping each clip independently)
        const minStart = Math.min(...st.group.map((g) => g.orig.start));
        let groupDelta = ns - orig.start;
        if (minStart + groupDelta < 0) groupDelta = -minStart;
        for (const g of st.group) {
          const gl = findClip(draft, g.id);
          if (gl) gl.clip.start = g.orig.start + groupDelta;
        }
        // vertical move: re-home the clip onto the hovered same-kind lane
        // (the draft is cloned from base each update, so the clip always
        // starts out on its origin track here)
        if (crossTrackId) moveClipToTrack(draft, clip.id, crossTrackId);
      } else if ('kind' in c) {
        // overlay clips: trim = move start/duration
        const oc = orig as OverlayClip;
        if (st.mode === 'trim-l') {
          let ns = snapTime(oc.start + dt, st.targets, threshold);
          ns = Math.min(Math.max(0, ns), oc.start + oc.duration - 0.1);
          c.start = ns;
          (c as OverlayClip).duration = oc.duration - (ns - oc.start);
        } else {
          let ne = snapTime(oc.start + oc.duration + dt, st.targets, threshold);
          ne = Math.max(oc.start + 0.1, ne);
          (c as OverlayClip).duration = ne - oc.start;
        }
      } else {
        // video/audio: trim adjusts source in/out
        const mc = c as VideoClip | AudioClip;
        const mo = orig as VideoClip | AudioClip;
        const speed = mo.speed ?? 1;
        const srcDur =
          draft.sources[mo.source]?.kind === 'pandora-video'
            ? (draft.sources[mo.source] as { duration: number }).duration
            : Infinity;
        if (st.mode === 'trim-l') {
          let ns = snapTime(mo.start + dt, st.targets, threshold);
          ns = Math.max(ns, mo.start - mo.in / speed); // can't reveal before source 0
          ns = Math.min(ns, mo.start + origDur - MIN_CLIP_DURATION);
          const delta = ns - mo.start;
          mc.start = ns;
          mc.in = mo.in + delta * speed;
        } else {
          const origEnd = mo.start + origDur;
          let ne = snapTime(origEnd + dt, st.targets, threshold);
          ne = Math.max(mo.start + MIN_CLIP_DURATION, ne);
          ne = Math.min(ne, mo.start + (srcDur - mo.in) / speed);
          mc.out = mo.in + (ne - mo.start) * speed;
        }
      }

      // ripple-trim (Alt + right handle): shift later clips on this track by the
      // change in this clip's end so downstream timing follows the trim. Right
      // edge only — left-edge ripple is ambiguous, so Alt+trim-l is a no-op.
      if (st.alt && st.mode === 'trim-r') {
        const origEnd = orig.start + origDur;
        const newEnd = c.start + clipDuration(c);
        if (newEnd !== origEnd) rippleLaterClips(loc.track, origEnd, newEnd - origEnd);
      }
    });
    setDropTrack(crossTrackId);
  }

  function endDrag() {
    const st = dragState.current;
    dragState.current = null;
    setDropTrack(null);
    if (!st) return;
    const tlState = useTimeline.getState();
    if (st.moved) {
      tlState.commitTransient();
    } else {
      tlState.select(clip.id, st.shift);
    }
  }

  const label =
    'kind' in clip
      ? clip.kind === 'text'
        ? clip.text.split('\n')[0]
        : 'image'
      : sourceTitle(source, clip.source);

  const sub = !('kind' in clip)
    ? `${formatTime((clip as VideoClip | AudioClip).in, true)} → ${formatTime(
        (clip as VideoClip | AudioClip).out,
        true,
      )}`
    : `${dur.toFixed(1)}s`;

  return (
    <div
      className={[
        'tl-clip',
        `tl-clip-${trackKind}`,
        selected ? 'selected' : '',
        'kind' in clip && clip.kind === 'text' ? 'tl-clip-textkind' : '',
      ].join(' ')}
      style={{ left, width }}
      onPointerDown={onPointerDown('move')}
    >
      {!('kind' in clip) && (
        <>
          <ClipStrip
            clip={clip as VideoClip | AudioClip}
            src={source}
            archives={archives}
            audio={trackKind === 'audio'}
          />
          <CacheBar clip={clip as VideoClip | AudioClip} />
        </>
      )}
      <div className="tl-clip-label">
        <span className="tl-clip-title">{label}</span>
        <span className="tl-clip-sub timecode">{sub}</span>
      </div>
      <div className="tl-clip-handle l" onPointerDown={onPointerDown('trim-l')} />
      <div className="tl-clip-handle r" onPointerDown={onPointerDown('trim-r')} />
      {selected && <KeyframeMarkers clip={clip} />}
    </div>
  );
});

/**
 * Diamond markers on a selected clip: union of keyframe times across all its
 * params, mapped from the keyframe axis (source time for media clips,
 * clip-local for overlays) to clip-local pixels. Dragging a diamond retimes
 * every keyframe at that instant (single undo entry via the transient trio).
 */
function KeyframeMarkers({ clip }: { clip: AnyClip }) {
  const { pps } = useTimelineCtx();
  const times = clipKeyframeTimes(clip);
  if (!times.length) return null;

  const isMedia = !('kind' in clip);
  const speed = isMedia ? ((clip as VideoClip | AudioClip).speed ?? 1) : 1;
  const axisToLocal = (axisT: number) =>
    isMedia ? (axisT - (clip as VideoClip | AudioClip).in) / speed : axisT;
  const bounds: [number, number] = isMedia
    ? [(clip as VideoClip | AudioClip).in, (clip as VideoClip | AudioClip).out]
    : [0, (clip as OverlayClip).duration];

  function onMarkerDown(axisT: number) {
    return (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      const startX = e.clientX;
      const tl = useTimeline.getState();
      let began = false;
      const onMove = (ev: PointerEvent) => {
        const dt = ((ev.clientX - startX) / pps) * (isMedia ? speed : 1); // axis delta
        if (!began) {
          if (Math.abs(ev.clientX - startX) < 3) return;
          began = true;
          tl.beginTransient();
        }
        const target = Math.max(bounds[0], Math.min(bounds[1], axisT + dt));
        useTimeline.getState().updateTransient((draft) => {
          const loc = findClip(draft, clip.id);
          if (loc) retimeKeyframesAt(loc.clip, axisT, target);
        });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (began) useTimeline.getState().commitTransient();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };
  }

  return (
    <>
      {times.map((axisT) => (
        <div
          key={axisT}
          className="tl-kf"
          style={{ left: axisToLocal(axisT) * pps }}
          title="Keyframe — drag to retime"
          onPointerDown={onMarkerDown(axisT)}
        />
      ))}
    </>
  );
}
